import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ILineItems } from '../../models/ILineItems';
import { LineItemType } from '../../models/line-item-type';
import { IShoppingCart } from '../../models/shopping-cart/i-shopping-cart';

const SHOPPING_CART_TTL = 1000 * 60 * 10 // 10 minutes;

@Injectable({
  providedIn: 'root'
})
export class ShoppingCartService {
  private cartSignal = signal<IShoppingCart>(this.getEmptyCart());

  public cart = toObservable(this.cartSignal);

  public getActiveCart(auctionId: number): IShoppingCart {
    let cart = this.getCart(auctionId);
    const isExpired = this.isExpired(cart);
    if (isExpired) {
      console.log("Cart expired. Resetting...");
      cart = this.getEmptyCart();
      this.saveCart(auctionId, cart);
    }
    this.cartSignal.set(cart);
    return cart;
  }

  public addElementToCart(auctionId: number, element: ILineItems) {
    switch (element.type) {
      case LineItemType.Donation:
        this.addDonationToCart(auctionId, element);
        this.cartSignal.set(this.getCart(auctionId));
        break;
      case LineItemType.Item:
        this.addItemToCart(auctionId, element);
        this.cartSignal.set(this.getCart(auctionId));
        break;
      case LineItemType.Ticket: // pass
      default:
        console.error("Unsupported element type: " + element.type + ". Not adding it to cart...");
    }
  }

  public updateElementInCart(auctionId: number, element: ILineItems) {
    const cart = this.getCart(auctionId);
    const toUpdate = cart.lineItems.find(x => x.id == element.id && x.type == element.type);
    if (toUpdate == undefined) {
      console.warn('Element not found in cart. Ignoring...');
      return;
    }
    toUpdate.quantity = element.quantity;
    this.saveCart(auctionId, cart);
  }

  public removeElementFromCart(auctionId: number, element: ILineItems) {
    const cart = this.getCart(auctionId);
    const toRemove = cart.lineItems.find(x => x.id == element.id && x.type == element.type);
    if (toRemove == undefined) {
      console.warn('Element not found in cart. Ignoring...');
      return;
    }
    cart.lineItems.splice(cart.lineItems.indexOf(toRemove), 1);
    this.saveCart(auctionId, cart);
    this.cartSignal.set(cart);
  }

  public clearCart(auctionId: number) {
    const cart = this.getEmptyCart();
    this.saveCart(auctionId, cart);
    this.cartSignal.set(cart);
  }

  public getEmptyCart(): IShoppingCart {
    return { lineItems: [], lastUpdated: new Date().getTime() };
  }

  public isElementFullyInCart(auctionId: number, lineItems: ILineItems[]): boolean {
    const cart = this.getCart(auctionId);
    let isFullyInCart = true;
    lineItems.forEach(element => {
      if (element.type == LineItemType.Item) {
        if (cart.lineItems.find(x => x.id == element.id && x.type == LineItemType.Item) == undefined) {
          isFullyInCart = false;
        }
      }
    });
    return isFullyInCart;
  }

  private getCart(auctionId: number): IShoppingCart {
    const storage = window.localStorage;
    const cartJson = storage.getItem('cart_' + auctionId);
    let cart: IShoppingCart;
    if (cartJson != null) {
      cart = JSON.parse(cartJson);
    } else {
      cart = this.getEmptyCart();
    }
    return cart;
  }

  private addItemToCart(auctionId: number, item: ILineItems) {
    const cart = this.getCart(auctionId);
    if (cart.lineItems.find(x => x.id == item.id && x.type == LineItemType.Item) == undefined) {
      cart.lineItems.push(item);
      this.saveCart(auctionId, cart);
    } else {
      console.info('Item already in cart. Ignoring...');
    }
  }

  private addDonationToCart(auctionId: number, donation: ILineItems) {
    const cart = this.getCart(auctionId);
    cart.lineItems.push(donation);
    this.saveCart(auctionId, cart);
  }

  private saveCart(auctionId: number, cart: IShoppingCart) {
    const storage = window.localStorage;
    cart.lastUpdated = new Date().getTime();
    storage.setItem('cart_' + auctionId, JSON.stringify(cart));
  }

  private isExpired(cart: IShoppingCart): boolean {
    let expired = cart.lastUpdated == null || cart.lastUpdated == undefined;
    expired = expired || (cart.lastUpdated + SHOPPING_CART_TTL) < new Date().getTime();
    return expired
  }
}
